import { useState, useEffect } from 'preact/hooks'
import { SpeakerImage } from './SpeakerImage'
import { formatTime } from '../utils/dateUtils'
import { getSpeakerEmoji } from '../utils/speakerUtils'

export function FilterTabs({ sessions, activeContentTypes, onContentTypesChange, onSpeakerClick }) {
  const [activeTab, setActiveTab] = useState('speakers')
  const [isExpanded, setIsExpanded] = useState(false)
  const [uniqueContentTypes, setUniqueContentTypes] = useState([])
  const [speakerStats, setSpeakerStats] = useState([])
  const [speakerSortBy, setSpeakerSortBy] = useState('sessions')
  const [speakerViewMode, setSpeakerViewMode] = useState('compact')

  // Extract unique content types with custom ordering
  useEffect(() => {
    const contentTypes = [...new Set(sessions
      .map(session => session.contentType)
      .filter(Boolean))]
    
    // Custom sort order: Round Table and Talks first, then alphabetical
    const priorityTypes = ['Round Table', 'Talks']
    const sortedTypes = contentTypes.sort((a, b) => {
      const aIndex = priorityTypes.indexOf(a)
      const bIndex = priorityTypes.indexOf(b)
      
      // If both are priority types, sort by priority order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }
      
      // If only a is priority, it comes first
      if (aIndex !== -1) return -1
      
      // If only b is priority, it comes first
      if (bIndex !== -1) return 1
      
      // Neither are priority, sort alphabetically
      return a.localeCompare(b)
    })
    
    setUniqueContentTypes(sortedTypes)
  }, [sessions])

  // Extract speaker statistics
  useEffect(() => {
    const speakerMap = new Map()
    
    sessions.forEach(session => {
      if (session.speakers && session.speakers.length > 0) {
        session.speakers.forEach(speaker => {
          if (speakerMap.has(speaker.name)) {
            speakerMap.get(speaker.name).sessionCount++
            speakerMap.get(speaker.name).sessions.push(session)
          } else {
            speakerMap.set(speaker.name, {
              ...speaker,
              sessionCount: 1,
              sessions: [session]
            })
          }
        })
      }
    })

    const speakers = Array.from(speakerMap.values())
    setSpeakerStats(speakers)
  }, [sessions])

  // Sort speakers based on current sort option
  const sortedSpeakers = [...speakerStats].sort((a, b) => {
    if (speakerSortBy === 'alphabetical') {
      return a.name.localeCompare(b.name)
    } else {
      // Sort by session count descending, then alphabetically
      if (a.sessionCount !== b.sessionCount) {
        return b.sessionCount - a.sessionCount
      }
      return a.name.localeCompare(b.name)
    }
  })

  const handleContentTypeToggle = (contentType) => {
    if (activeContentTypes.includes('__NONE_SELECTED__')) {
      onContentTypesChange([contentType])
      return
    }

    if (activeContentTypes.length === 0) {
      onContentTypesChange([contentType])
      return
    }

    const updated = activeContentTypes.includes(contentType)
      ? activeContentTypes.filter(type => type !== contentType)
      : [...activeContentTypes, contentType]
    
    onContentTypesChange(updated)
  }

  const handleSelectAllContent = () => {
    onContentTypesChange([])
  }

  const handleClearAllContent = () => {
    onContentTypesChange(['__NONE_SELECTED__'])
  }

  const handleSpeakerClick = (speaker) => {
    onSpeakerClick?.(speaker)
  }

  if (uniqueContentTypes.length === 0 && speakerStats.length === 0) return null

  const isShowingAll = activeContentTypes.length === 0 && !activeContentTypes.includes('__NONE_SELECTED__')
  const isShowingNone = activeContentTypes.includes('__NONE_SELECTED__')

  return (
    <div className="mb-4">
      <div className="bg-editions-dark-light/50 rounded-xl border border-editions-purple/20">
        {/* Header with expand/collapse */}
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors rounded-xl"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <h4 className="text-white font-medium text-sm">
              {activeTab === 'speakers' ? 'Filters by Speaker' : 'Filters by Content'}
            </h4>
            <span className="text-xs text-gray-400">
              ({activeTab === 'speakers' 
                ? `${speakerStats.length} speakers`
                : `${isShowingNone ? '0' : isShowingAll ? uniqueContentTypes.length : activeContentTypes.length} of ${uniqueContentTypes.length} content types`
              })
            </span>
          </div>
          <div className="flex items-center gap-3">
            {!isExpanded && activeTab === 'content' && (
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSelectAllContent()
                  }}
                  disabled={isShowingAll}
                  className="text-xs text-editions-purple hover:text-editions-purple-light disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                >
                  Select All
                </button>
                <span className="text-gray-500 text-xs">|</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClearAllContent()
                  }}
                  disabled={isShowingNone}
                  className="text-xs text-editions-purple hover:text-editions-purple-light disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                >
                  Clear All
                </button>
              </div>
            )}
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4">
            {/* Tab Navigation */}
            <div className="flex gap-1 mb-4 pt-3 border-t border-white/10">
              <button
                onClick={() => setActiveTab('speakers')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === 'speakers'
                    ? 'bg-editions-purple/20 text-editions-purple-light border border-editions-purple/40'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                👤 Speakers ({speakerStats.length})
              </button>
              <button
                onClick={() => setActiveTab('content')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === 'content'
                    ? 'bg-editions-purple/20 text-editions-purple-light border border-editions-purple/40'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                📋 Content Type
              </button>
            </div>

            {/* Speakers Tab */}
            {activeTab === 'speakers' && (
              <div>
                {/* Speaker Controls */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <span className="text-xs text-gray-400">Browse speakers and their sessions:</span>
                  
                  <div className="flex items-center justify-end gap-3">
                    {/* Sort Options */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Sort:</span>
                      <select
                        value={speakerSortBy}
                        onChange={(e) => setSpeakerSortBy(e.target.value)}
                        className="text-xs bg-white/5 border border-white/20 rounded px-2 py-1 text-white focus:border-editions-purple/50 focus:outline-none"
                      >
                        <option value="sessions">By Sessions</option>
                        <option value="alphabetical">A to Z</option>
                      </select>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                      <button
                        onClick={() => setSpeakerViewMode('compact')}
                        className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                          speakerViewMode === 'compact'
                            ? 'bg-editions-purple/30 text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        Grid
                      </button>
                      <button
                        onClick={() => setSpeakerViewMode('detailed')}
                        className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                          speakerViewMode === 'detailed'
                            ? 'bg-editions-purple/30 text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        List
                      </button>
                    </div>
                  </div>
                </div>

                {/* Speaker Display */}
                {speakerViewMode === 'compact' ? (
                  // Compact Grid View
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                    {sortedSpeakers.map(speaker => (
                      <button
                        key={speaker.name}
                        onClick={() => handleSpeakerClick(speaker)}
                        className="flex flex-col items-center gap-2 p-3 rounded-lg text-center
                                 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-editions-purple/30
                                 transition-all duration-200 transform hover:scale-[1.02]"
                      >
                        <SpeakerImage speaker={speaker} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white text-xs truncate">
                            {speaker.name}
                          </div>
                          {speaker.title && (
                            <div className="text-xs text-gray-400 truncate mt-1 flex items-center gap-1">
                              <span>{getSpeakerEmoji(speaker.title)}</span>
                              <span>{speaker.title}</span>
                            </div>
                          )}
                          <div className="text-xs text-editions-orange mt-1">
                            {speaker.sessionCount} session{speaker.sessionCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  // Detailed List View
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {sortedSpeakers.map(speaker => (
                      <div
                        key={speaker.name}
                        className="bg-white/5 border border-white/10 rounded-lg p-4"
                      >
                        {/* Speaker Header */}
                        <div className="flex items-center gap-3 mb-3">
                          <SpeakerImage speaker={speaker} size="sm" />
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => handleSpeakerClick(speaker)}
                              className="font-medium text-white text-sm hover:text-editions-purple-light transition-colors text-left"
                            >
                              {speaker.name}
                            </button>
                            {speaker.title && (
                              <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                <span>{getSpeakerEmoji(speaker.title)}</span>
                                <span>{speaker.title}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-editions-orange">
                            {speaker.sessionCount} session{speaker.sessionCount !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* Speaker Sessions */}
                        <div className="space-y-2">
                          {speaker.sessions
                            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                            .map(session => (
                            <div
                              key={session.id}
                              className="flex items-center justify-between gap-3 p-2 bg-white/5 rounded border border-white/10"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-white truncate">
                                  {session.title}
                                </div>
                                <div className="text-xs text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                                  <span className="flex items-center gap-1">📍 {session.location}</span>
                                  <span>•</span>
                                  <span className="flex items-center gap-1 whitespace-nowrap">🕒 {formatTime(session.startTime)}</span>
                                </div>
                              </div>
                              <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                                session.category === 'Dev mode' 
                                  ? 'bg-editions-green/20 text-editions-green'
                                  : session.category === 'Biz mode'
                                  ? 'bg-editions-purple/20 text-editions-purple-light'
                                  : 'bg-gray-600/20 text-gray-300'
                              }`}>
                                {session.category}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {speakerStats.length === 0 && (
                  <div className="text-center py-4 text-gray-400">
                    <span className="text-2xl block mb-2">👤</span>
                    <p className="text-sm">No speakers found</p>
                  </div>
                )}
              </div>
            )}

            {/* Content Type Tab */}
            {activeTab === 'content' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-400">Select content types:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSelectAllContent}
                      disabled={isShowingAll}
                      className="text-xs text-editions-purple hover:text-editions-purple-light disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                    >
                      Select All
                    </button>
                    <span className="text-gray-500 text-xs">|</span>
                    <button
                      onClick={handleClearAllContent}
                      disabled={isShowingNone}
                      className="text-xs text-editions-purple hover:text-editions-purple-light disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {uniqueContentTypes.map(contentType => {
                    const isSelected = !isShowingNone && (activeContentTypes.length === 0 || activeContentTypes.includes(contentType))
                    const sessionCount = sessions.filter(s => s.contentType === contentType).length

                    const displayName = contentType
                      .replace(/&/g, 'and')
                      .replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())

                    return (
                      <label
                        key={contentType}
                        className={`
                          flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200
                          hover:scale-[1.02] transform
                          ${isSelected
                            ? 'bg-editions-purple/20 border border-editions-purple/40 shadow-lg shadow-editions-purple/20'
                            : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-editions-purple/20'
                          }
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleContentTypeToggle(contentType)}
                          className="w-4 h-4 rounded border-2 border-editions-purple/50 
                                   bg-transparent checked:bg-editions-purple checked:border-editions-purple
                                   focus:ring-2 focus:ring-editions-purple/20 transition-colors"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">
                            {displayName}
                          </div>
                          <div className="text-xs text-gray-400">
                            {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>

                {/* Active filters indicator */}
                {!isShowingAll && !isShowingNone && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-xs text-gray-400 mb-2">
                      Showing {activeContentTypes.length} of {uniqueContentTypes.length} content types
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {activeContentTypes.map(type => {
                        const displayName = type
                          .replace(/&/g, 'and')
                          .replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        
                        return (
                          <span
                            key={type}
                            className="text-xs bg-editions-purple/20 text-editions-purple-light px-2 py-1 rounded-full"
                          >
                            {displayName}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}

                {isShowingNone && (
                  <div className="mt-3 pt-3 border-t border-white/10 text-center">
                    <div className="text-xs text-gray-400">
                      No content types selected - no sessions will be shown
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
} 